// NOTE: Adriel move to TS package
import * as React from 'react';
import cx from 'classnames';
import { compose } from 'redux';

// Components
import { TimingBeacon } from '@shopee/prometheus/src/beacon';
import MartDisclaimerPopup from './components/MartDisclaimerPopup';
import TabsGroup from '@shopee/rw-search-tabs-group';
import getSortTabHeaders from './components/SearchItemsTabsHeader';
import MartSearchResultMessage from './components/MartSearchResultMessage';
import MartSearchResultJsonLd from './components/MartSearchResultJsonLd';
import ItemCardList from '@shopee/rw-item-card-list';
import { Item, SMartItemCardV1 as ItemCard } from '@shopee/item-card-s-mart-v2';
import {
  withOnTrackAsProps,
  TrackingDataAnchor,
  trackingAction,
} from '@shopee/tracking-hoc';
import { AddToCartContext, withAddToCart } from '@shopee/rw-add-to-cart-drawer';
import { withMartSearchResultKey } from './hoc';
import { AccountStatus, withAccountInfo } from '@shopee/account-hoc';

// Utils
import { getLocale } from '@shopee/global-env';
import { I18n, Tracking } from '@shopee/platform-rw';
import {
  handleNewKeywordSearch,
  handleNoCorrectionInSearchParams,
} from './utils';
import { useMartSearchItemsInfiniteLoading as useMartSearchItems } from '@shopee/mart-search-items-hooks';

import { MART_SEARCH_ITEMS_REDUX_KEY } from '@shopee/rw-search-redux-keys';
import {
  resetSearchParams,
  SEARCH_NO_RESULT_MAP,
  SEARCH_RESULT_TYPE,
  SORT_METHOD,
} from '@shopee/search-common-util';
import PROGRESS, { ProgressType } from '@shopee/api-progress';
import { SHOPEE_MART } from '@shopee/colors';
import { ColorThemeContext } from './context';

// Types
import type { WithAccountInfoProps } from '@shopee/account-hoc';
import type { SearchParams } from '@shopee/search-common-util';
import type { ItemBasic, ItemSearch } from '@shopee/flow-types';
import type { OnTrackType } from '@shopee/tracking-hoc';
import type { MartSearchItemsStore } from '@shopee/mart-search-items-redux';
import type {
  MartSearchItemsParams,
  CustomMartSearchItemsApi,
} from '@shopee/mart-search-items-redux';

import TrackingRules from './MartSearchResult.track';

import s from './MartSearchResult.scss';

import { LABELS_SET, useElementsApi } from '@shopee/item-card-elements';
import {
  isXiapibuyDomain,
  SeeMoreLogInButton,
} from '@shopee/search-rcmd-common';

Tracking.injectTracking(TrackingRules as unknown as string);

type OwnProps = {
  searchParams: SearchParams;
  headerTop?: string | number;
  topModule?: JSX.Element | React.ReactNode;
  sortMethods?: SORT_METHOD[];
  topModuleIsLoading?: boolean;
  className?: string;
  innerRef?: React.Ref<HTMLDivElement>;
  scrollToTopOnChangeSortType?: boolean;
  displaySeoLdJson?: boolean;
  onSearchParamsChange: (newParam: SearchParams, pushHistory?: boolean) => void;
  contextOnTrack?: OnTrackType;
  getCustomMartSearchItemsApi?: (
    params: MartSearchItemsParams
  ) => CustomMartSearchItemsApi | undefined;
};
type Props = OwnProps & WithAccountInfoProps;

const LOCALE = getLocale();

const { t, withI18nCollections } = I18n;

const SEARCH_RESULT_T_COLLECTION = [101];

const ITEMS_FETCH_BATCH = 10;

const ANTI_CRAWLER_RESTRICTED_NUMBER = 40;

const NO_RESULT_SEARCH_TYPES = [
  ...SEARCH_NO_RESULT_MAP,
  SEARCH_RESULT_TYPE.NO_RESULT_FILTER,
];

const LOADING_PROGRESS: ProgressType[] = [PROGRESS.INIT, PROGRESS.REQ];

const SORT_METHODS = [
  SORT_METHOD.RELEVANCY,
  SORT_METHOD.DISCOUNT,
  SORT_METHOD.SALES,
  SORT_METHOD.PRICE,
];

function getMartSearchItemsStore(state: Record<string, unknown>) {
  return state[MART_SEARCH_ITEMS_REDUX_KEY] as MartSearchItemsStore;
}

const MartSearchResult = ({
  searchParams,
  headerTop = 0,
  topModule,
  className,
  innerRef,
  scrollToTopOnChangeSortType = true,
  displaySeoLdJson,
  onSearchParamsChange,
  contextOnTrack,
  topModuleIsLoading,
  getCustomMartSearchItemsApi,
  accountStatus,
}: Props) => {
  const { items, offset, total, progress, resultType, handleLoadMore } =
    useMartSearchItems({
      reduxKey: MART_SEARCH_ITEMS_REDUX_KEY,
      searchParams,
      limit: ITEMS_FETCH_BATCH,
      getStore: getMartSearchItemsStore,
      onQueryRewrite: onSearchParamsChange,
      getCustomMartSearchItemsApi,
    });

  const handleSortTypeChange = React.useCallback(
    (newParams: SearchParams) => {
      let mergedParams = {
        ...searchParams,
        ...newParams,
      };
      mergedParams = handleNewKeywordSearch({
        prevParams: searchParams,
        nextParams: mergedParams,
      });
      mergedParams = handleNoCorrectionInSearchParams({
        prevParams: searchParams,
        nextParams: mergedParams,
        resultType,
        isOfficialMall: false,
      });

      trackingAction({
        action: 'applyMartSortOption',
        data: {
          searchParams: mergedParams,
        },
        track: contextOnTrack ?? (() => Promise.resolve()),
      });

      onSearchParamsChange(mergedParams);

      if (scrollToTopOnChangeSortType) {
        requestAnimationFrame(() => {
          window.scroll(0, 0);
        });
      }
    },
    [
      contextOnTrack,
      onSearchParamsChange,
      resultType,
      searchParams,
      scrollToTopOnChangeSortType,
    ]
  );
  const handleNewSearch = React.useCallback(
    (newParams: SearchParams) => {
      const mergedParams = {
        ...resetSearchParams(searchParams),
        ...newParams,
      };
      onSearchParamsChange(mergedParams);
    },
    [onSearchParamsChange, searchParams]
  );

  const hasMore = offset + ITEMS_FETCH_BATCH < total;
  const isLoading = LOADING_PROGRESS.includes(progress) || topModuleIsLoading;
  const shouldDisplayTabs = !SEARCH_NO_RESULT_MAP.includes(
    resultType as unknown as SEARCH_RESULT_TYPE
  );
  const shouldDisplayItems = !NO_RESULT_SEARCH_TYPES.includes(
    resultType as unknown as SEARCH_RESULT_TYPE
  );
  const sortMethods = SORT_METHODS;
  const activeIndex = Math.max(
    sortMethods.indexOf(searchParams.sortBy as SORT_METHOD),
    0
  );
  const sortTabs = React.useMemo(() => {
    return getSortTabHeaders(searchParams, handleSortTypeChange, sortMethods, {
      shouldSendActionTracking: false,
    });
  }, [handleSortTypeChange, searchParams, sortMethods]);

  const isLoggedIn = accountStatus === AccountStatus.LOGGED_IN;
  const checkXiapibuyDomain = isXiapibuyDomain(window.location.href);
  const displayedItems =
    isLoggedIn || checkXiapibuyDomain
      ? items
      : items.slice(0, ANTI_CRAWLER_RESTRICTED_NUMBER);
  const customLoggedInSeeMoreButton = (isLoggedIn: boolean) => {
    return () => (!isLoggedIn ? <SeeMoreLogInButton /> : null);
  };

  const elementsData = useElementsApi(LABELS_SET.S_MART);
  const requestAddItemToCart = React.useContext(AddToCartContext);
  const renderItemCard = React.useCallback(
    (item: ItemBasic | ItemSearch, index: number) => (
      <TrackingDataAnchor
        key={item.itemid}
        targetData={{ index, propsItem: item }}
      >
        <div className={s.itemWrapper}>
          <ItemCard
            index={index}
            item={item as Item}
            elementsData={elementsData}
            enableAdultMask={!__SERVER_RENDER__}
            requestAddItemToCart={requestAddItemToCart}
          />
        </div>
      </TrackingDataAnchor>
    ),
    [requestAddItemToCart, elementsData]
  );

  return (
    <ColorThemeContext.Provider value={SHOPEE_MART}>
      <div className={cx(s.container, className)} ref={innerRef}>
        <TimingBeacon />
        {LOCALE === 'VN' && <MartDisclaimerPopup searchParams={searchParams} />}
        {shouldDisplayTabs && (
          <TabsGroup
            header={sortTabs}
            activeIndex={activeIndex}
            headerTop={headerTop}
          />
        )}

        <div key="tabContent">
          {shouldDisplayItems ? topModule : null}

          <MartSearchResultMessage
            resultType={resultType}
            searchParams={searchParams}
            onSearchParamsChange={handleNewSearch}
          />

          {shouldDisplayItems && (
            <>
              <ItemCardList
                customLogInSeeMore={{
                  shouldDisplayCustomLogInSeeMoreButton:
                    !checkXiapibuyDomain &&
                    !isLoggedIn &&
                    displayedItems.length >= ANTI_CRAWLER_RESTRICTED_NUMBER,
                  component: customLoggedInSeeMoreButton(isLoggedIn),
                }}
                items={displayedItems as ItemSearch[]}
                hasMore={hasMore}
                isShowNoMore={true}
                loading={isLoading}
                loadMore={handleLoadMore}
                labelNoMore={t('label_search_no_more_items')}
                renderItemCard={renderItemCard}
                colorTheme={SHOPEE_MART}
                className={s.itemList}
                withSeo={displaySeoLdJson}
              />
              {!!displaySeoLdJson && (
                <MartSearchResultJsonLd
                  items={displayedItems as ItemSearch[]}
                />
              )}
            </>
          )}
        </div>
      </div>
    </ColorThemeContext.Provider>
  );
};

export default compose(
  withI18nCollections(SEARCH_RESULT_T_COLLECTION),
  withAddToCart,
  withAccountInfo(),
  withOnTrackAsProps('contextOnTrack'),
  withMartSearchResultKey
)(MartSearchResult) as React.ComponentType<OwnProps>;
